import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent } from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Fullscreen,
  FullscreenExit,
} from '@mui/icons-material';

// Import your backend URL constant
import API_BASE_URL from '../../../../Constants'; 
// Adjust the path above to wherever your Constants file is located.

/**
 * InspectorEntryForm
 *
 * Props:
 *   - open: boolean to control if the modal is open
 *   - onClose: function to close the modal
 *   - refreshInspectors: (optional) callback to reload the inspectors list after creation
 */
const InspectorEntryForm = ({ open, onClose, refreshInspectors }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  // Minimal form data
  const [inspectorName, setInspectorName] = useState('');
  const [certificationNumber, setCertificationNumber] = useState('');
  const [isDirector, setIsDirector] = useState(false);

  // Reset state when the dialog closes
  useEffect(() => {
    if (!open) {
      setInspectorName('');
      setCertificationNumber('');
      setIsDirector(false);
    }
  }, [open]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Basic client-side validation for required fields:
    if (!inspectorName.trim()) {
      alert('Inspector Name is required!');
      return;
    }

    // Build the payload (matching the Spring `Inspector` fields)
    const payload = {
      inspectorName,
      certificationNumber,
      isDirector,
      // inspectorId is omitted because the DB auto-generates it
      // any other fields are omitted
    };

    try {
      // Use API_BASE_URL and append /api/inspectors
      const url = `${API_BASE_URL}/api/inspectors`;
      const resp = await fetch(url, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      if (!resp.ok) {
        throw new Error(`Failed to create inspector. Status: ${resp.status}`);
      }

      // Optionally refresh the inspectors list in the parent
      if (refreshInspectors) refreshInspectors();

      // Close the modal
      onClose();
    } catch (error) {
      console.error('Error creating inspector:', error);
      alert('Failed to create inspector.');
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={isExpanded ? false : 'sm'}
      fullWidth
      PaperProps={{
        style: {
          width: isExpanded ? '90%' : '600px',
        },
      }}
    >
      {/* Sticky Header */}
      <div className="sticky top-0 bg-white z-10">
        <div className="flex justify-between items-center p-4">
          {/* Left side: back arrow & title */}
          <div className="flex items-center space-x-4">
            <button
              onClick={onClose}
              className="p-2 rounded-full hover:bg-gray-200 transition"
              aria-label="Back"
            >
              <ArrowBackIcon />
            </button>
            <h2 className="text-2xl font-bold ml-4">
              Certified Inspector Entry
            </h2>
          </div>
          {/* Right side: expand/shrink */}
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="p-2 rounded-full bg-white hover:bg-gray-200 transition"
            aria-label={isExpanded ? 'Shrink Modal' : 'Expand Modal'}
          >
            {isExpanded ? <FullscreenExit /> : <Fullscreen />}
          </button>
        </div>
        {/* Submit button row */}
        <div className="flex space-x-4 p-4">
          <button
            type="submit"
            form="inspectorForm"
            className="px-6 py-2 bg-yellow-400 text-blue-700 font-bold rounded-md hover:bg-yellow-500 transition"
          >
            Save
          </button>
        </div>
      </div>

      {/* Body */}
      <DialogContent dividers>
        <form
          id="inspectorForm"
          onSubmit={handleSubmit}
          className="space-y-4"
        >
          {/* Inspector Name */}
          <div>
            <label className="block font-semibold text-red-600">
              Inspector Name (required)
            </label>
            <input
              type="text"
              name="inspectorName"
              className="border p-2 w-full rounded"
              value={inspectorName}
              onChange={(e) => setInspectorName(e.target.value)}
              placeholder="Enter Inspector's Name"
            />
          </div>

          {/* Certification Number */}
          <div>
            <label className="block font-semibold">
              Certification Number
            </label>
            <input
              type="text"
              name="certificationNumber"
              className="border p-2 w-full rounded"
              value={certificationNumber}
              onChange={(e) => setCertificationNumber(e.target.value)}
              placeholder="Enter Certification #"
            />
          </div>

          {/* Is Director? (checkbox) */}
          <div className="flex items-center space-x-3">
            <label htmlFor="isDirector" className="font-semibold">
              Is Director?
            </label>
            <input
              type="checkbox"
              name="isDirector"
              id="isDirector"
              checked={isDirector}
              onChange={(e) => setIsDirector(e.target.checked)}
            />
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default InspectorEntryForm;
